import { formatInTimeZone } from "date-fns-tz";
import {
  formatIsoDateToDDMMYYYY,
  formatTo12HourTime,
  isScheduledDateTimeInFuture,
} from "../../config/utilities/fDate.js";
import {
  CAMPAIGN_AUDIENCE_OPTIONS,
  CAMPAIGN_CAMPUS_GRADE_OPTIONS,
  CAMPAIGN_DELIVERY_OPTIONS,
} from "./campaignFormConfig.js";

export const getDefaultAudienceFields = () => ({
  scheduleId: "",
  scheduleIds: [],
  tripTypeIds: [],
  tripTypeScope: "all",
  tripTypeDate: "",
  familyIds: [],
  stopIds: [],
  campus: "",
});

export const getTripTypeLabel = (tripTypeId, tripTypes = []) => {
  if (tripTypeId === undefined || tripTypeId === null || tripTypeId === "") {
    return "";
  }
  if (typeof tripTypeId === "object") {
    return tripTypeId.name || tripTypeId.label || tripTypeId.id || "";
  }
  const match = (tripTypes || []).find(
    (type) => type?.id === tripTypeId || type?.name === tripTypeId,
  );
  return match?.name || match?.label || String(tripTypeId);
};

export const clearAudienceFieldsForType = (audienceType) => {
  const cleared = getDefaultAudienceFields();
  if (audienceType === "schedule") {
    return {
      ...cleared,
      scheduleIds: [],
      scheduleId: "",
    };
  }
  if (audienceType === "tripType") {
    return {
      ...cleared,
      tripTypeIds: [],
      tripTypeScope: "all",
      tripTypeDate: "",
    };
  }
  if (audienceType === "family") {
    return { ...cleared, familyIds: [] };
  }
  if (audienceType === "stop") {
    return { ...cleared, stopIds: [] };
  }
  if (audienceType === "campus") {
    return { ...cleared, campus: "" };
  }
  return cleared;
};

export const getCampusLabel = (campus) => {
  const normalized = String(campus || "").trim().toLowerCase();
  const match = CAMPAIGN_CAMPUS_GRADE_OPTIONS.find(
    (option) => option.value === normalized,
  );
  return match?.label || campus || "";
};

/** @deprecated Use getCampusLabel */
export const getCampusGradeLabel = getCampusLabel;

export const resolveCampusValue = (input = {}) => {
  if (input.campus?.trim()) {
    return String(input.campus).trim().toLowerCase();
  }
  if (input.grade?.trim()) {
    return String(input.grade).trim().toLowerCase();
  }
  const detail = Array.isArray(input.campusDetails)
    ? input.campusDetails[0]
    : null;
  if (detail?.campus) {
    const normalized = String(detail.campus).trim().toLowerCase();
    if (normalized === "primary" || normalized === "secondary") {
      return normalized;
    }
  }
  return "";
};

export const stripHtmlToPlainText = (html) => {
  if (!html || typeof html !== "string") return "";
  if (typeof document === "undefined") {
    return html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  }
  const div = document.createElement("div");
  div.innerHTML = html;
  return (div.textContent || "").replace(/\s+/g, " ").trim();
};

export const getDeliveryLabel = (alertType) =>
  CAMPAIGN_DELIVERY_OPTIONS.find((o) => o.value === alertType)?.title || "—";

export const getAudienceLabel = (targetAudience) =>
  CAMPAIGN_AUDIENCE_OPTIONS.find((o) => o.value === targetAudience)?.title ||
  "—";

export const isAudienceSelectionValid = (input) => {
  const audience = input.targetAudience?.trim();
  if (!audience) return false;

  switch (audience) {
    case "all":
      return true;
    case "schedule":
      return (
        Array.isArray(input.scheduleIds) && input.scheduleIds.length > 0
      );
    case "tripType":
      if (!Array.isArray(input.tripTypeIds) || input.tripTypeIds.length === 0) {
        return false;
      }
      if (input.tripTypeScope === "date") {
        return Boolean(input.tripTypeDate?.trim());
      }
      return true;
    case "family":
      return Array.isArray(input.familyIds) && input.familyIds.length > 0;
    case "stop":
      return Array.isArray(input.stopIds) && input.stopIds.length > 0;
    case "campus":
      return Boolean(resolveCampusValue(input));
    default:
      return false;
  }
};

export const getAudienceSelectionError = (input) => {
  const audience = input.targetAudience;
  if (!audience?.trim()) return "Select who should receive this campaign.";

  switch (audience) {
    case "schedule":
      if (!Array.isArray(input.scheduleIds) || input.scheduleIds.length === 0) {
        return "Select at least one scheduled trip.";
      }
      return "";
    case "tripType":
      if (!Array.isArray(input.tripTypeIds) || input.tripTypeIds.length === 0) {
        return "Select at least one trip type.";
      }
      if (input.tripTypeScope === "date" && !input.tripTypeDate?.trim()) {
        return "Select a date for trip type targeting.";
      }
      return "";
    case "family":
      if (!Array.isArray(input.familyIds) || input.familyIds.length === 0) {
        return "Choose at least one family.";
      }
      return "";
    case "stop":
      if (!Array.isArray(input.stopIds) || input.stopIds.length === 0) {
        return "Choose at least one stop.";
      }
      return "";
    case "campus":
      if (!resolveCampusValue(input)) {
        return "Select a campus.";
      }
      return "";
    default:
      return "";
  }
};

export const formatDeliverySummary = (input, institutionTz) => {
  if (
    input.alertType === "scheduled" &&
    input.scheduledDate &&
    input.scheduledTime
  ) {
    const date = formatIsoDateToDDMMYYYY(input.scheduledDate);
    const time =
      formatTo12HourTime(input.scheduledTime) || input.scheduledTime;
    return `${date} at ${time}`;
  }
  if (input.alertType) {
    return getDeliveryLabel(input.alertType);
  }
  return "Not set";
};

export const formatAudienceSummary = (
  input,
  {
    scheduleOptions = [],
    tripTypes = [],
    familyLabels = {},
    stopLabels = {},
    scheduleLabels = {},
  } = {},
) => {
  switch (input.targetAudience) {
    case "all":
      return "Everyone in institution";
    case "schedule": {
      const ids = input.scheduleIds || [];
      if (ids.length === 0) return "No trips selected";
      const names = ids.map((id) =>
        resolveScheduleLabel(id, {
          scheduleLabels,
          scheduleDetails: input.scheduleDetails,
          scheduleOptions,
        }),
      );
      return `${ids.length} trip${ids.length === 1 ? "" : "s"}: ${names.slice(0, 2).join(", ")}${names.length > 2 ? ` +${names.length - 2}` : ""}`;
    }
    case "tripType": {
      const ids = input.tripTypeIds || [];
      const names = ids
        .map((id) => getTripTypeLabel(id, tripTypes))
        .filter(Boolean);
      const scope =
        input.tripTypeScope === "date" && input.tripTypeDate
          ? ` on ${input.tripTypeDate}`
          : " (upcoming trips)";
      return names.length > 0
        ? `Trip types: ${names.join(", ")}${scope}`
        : "No trip types selected";
    }
    case "family": {
      const ids = input.familyIds || [];
      if (ids.length === 0) return "No families selected";
      const names = ids.map((id) =>
        resolveFamilyLabel(id, {
          familyLabels,
          familyDetails: input.familyDetails,
        }),
      );
      return `${ids.length} famil${ids.length === 1 ? "y" : "ies"}: ${names.slice(0, 2).join(", ")}${names.length > 2 ? ` +${names.length - 2}` : ""}`;
    }
    case "stop": {
      const ids = input.stopIds || [];
      if (ids.length === 0) return "No stops selected";
      const names = ids.map((id) =>
        resolveStopLabel(id, {
          stopLabels,
          stopDetails: input.stopDetails,
        }),
      );
      return `${ids.length} stop${ids.length === 1 ? "" : "s"}: ${names.slice(0, 2).join(", ")}${names.length > 2 ? ` +${names.length - 2}` : ""}`;
    }
    case "campus": {
      const campus = resolveCampusValue(input);
      if (!campus) return "No campus selected";
      return `Campus: ${getCampusLabel(campus)}`;
    }
    default:
      return "—";
  }
};

export const getAudienceSummaryDisplay = (
  input,
  {
    scheduleOptions = [],
    tripTypes = [],
    familyLabels = {},
    stopLabels = {},
    scheduleLabels = {},
  } = {},
) => {
  const selection = formatAudienceSummary(input, {
    scheduleOptions,
    tripTypes,
    familyLabels,
    stopLabels,
    scheduleLabels,
  });

  if (!input.targetAudience?.trim()) {
    return "Select an audience";
  }

  if (input.targetAudience === "all") {
    return "All parents in this institution";
  }

  return selection;
};

export const formatScheduleStartTime = (time) => {
  if (!time) return "";

  const str = String(time).trim();
  if (/AM|PM/i.test(str)) {
    const match = str.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)$/i);
    if (match) {
      let hours = parseInt(match[1], 10);
      const minutes = parseInt(match[2], 10);
      const period = match[3].toUpperCase();
      if (period === "PM" && hours !== 12) hours += 12;
      if (period === "AM" && hours === 12) hours = 0;
      return formatTo12HourTime(`${hours}:${minutes}`) || str;
    }
    return str;
  }

  return formatTo12HourTime(str) || str;
};

export const formatScheduleOptionLabel = (schedule) => {
  const tripName = schedule?.tripDetails?.tripName || "Unnamed trip";
  const tripType =
    schedule?.tripDetails?.tripTypeName ||
    schedule?.tripDetails?.triptypeName ||
    "";
  const startTime = formatScheduleStartTime(
    schedule?.startTime ||
      schedule?.tripDetails?.startTime ||
      schedule?.scheduleStartTime,
  );
  const parts = [tripName];
  if (tripType) parts.push(tripType);
  if (startTime) parts.push(startTime);
  return parts.join(" · ");
};

export const getScheduleShortLabel = (schedule) =>
  schedule?.tripDetails?.tripName || "Unnamed trip";

export const getScheduleDisplayLabel = (schedule) => {
  const parts = [];
  const tripType =
    schedule?.tripDetails?.tripTypeName ||
    schedule?.tripDetails?.triptypeName ||
    "";
  const startTime = formatScheduleStartTime(
    schedule?.startTime ||
      schedule?.tripDetails?.startTime ||
      schedule?.scheduleStartTime,
  );
  if (tripType) parts.push(tripType);
  if (startTime) parts.push(startTime);
  if (
    schedule?.totalStudents !== undefined &&
    schedule?.totalStudents !== null
  ) {
    const count = Number(schedule.totalStudents);
    if (!Number.isNaN(count)) {
      parts.push(`${count} student${count === 1 ? "" : "s"}`);
    }
  }
  return parts.join(" · ");
};

export const mapScheduleToAudienceOption = (schedule) => ({
  value: schedule._id,
  label: formatScheduleOptionLabel(schedule),
  schedule,
});

export const getScheduleDetailShortLabel = (detail) =>
  detail?.scheduleName || detail?.name || "Unnamed trip";

export const formatScheduleDetailLabel = (detail) => {
  if (!detail) return "Unnamed trip";
  const name = getScheduleDetailShortLabel(detail);
  const tripType = detail.tripTypeName || detail.tripType || "";
  const startTime = formatScheduleStartTime(
    detail.scheduleStartTime || detail.startTime,
  );
  const parts = [name];
  if (tripType) parts.push(tripType);
  if (startTime) parts.push(startTime);
  return parts.join(" · ");
};

export const scheduleDetailToSchedule = (detail) => {
  const id = detail?.scheduleId || detail?.id || detail?._id;
  if (!id) return null;
  return {
    _id: id,
    tripDetails: {
      tripName: detail.scheduleName || detail.name,
      tripTypeName: detail.tripTypeName || detail.tripType,
      triptypeName: detail.tripTypeName || detail.tripType,
      startTime: detail.scheduleStartTime || detail.startTime,
    },
    scheduleStartTime: detail.scheduleStartTime || detail.startTime,
    startTime: detail.scheduleStartTime || detail.startTime,
  };
};

export const mapScheduleDetailsToAudienceOptions = (scheduleDetails) =>
  (Array.isArray(scheduleDetails) ? scheduleDetails : [])
    .map((detail) => {
      const schedule = scheduleDetailToSchedule(detail);
      if (!schedule) return null;
      return mapScheduleToAudienceOption(schedule);
    })
    .filter(Boolean);

export const buildScheduleLabelsFromDetails = (scheduleDetails) => {
  if (!Array.isArray(scheduleDetails)) return {};

  return scheduleDetails.reduce((labels, detail) => {
    const id = detail?.scheduleId || detail?.id || detail?._id;
    if (id) {
      labels[id] = formatScheduleDetailLabel(detail);
    }
    return labels;
  }, {});
};

export const resolveScheduleLabel = (
  id,
  { scheduleLabels = {}, scheduleDetails = [], scheduleOptions = [] } = {},
) => {
  if (scheduleLabels[id]) return scheduleLabels[id];

  const option = scheduleOptions.find((o) => o.value === id);
  if (option?.label) return option.label;

  const detail = scheduleDetails.find(
    (schedule) => (schedule?.scheduleId || schedule?.id || schedule?._id) === id,
  );
  if (detail) return formatScheduleDetailLabel(detail);

  return id;
};

export const getFamilyChildren = (family) =>
  family?.children || family?.child || [];

export const getFamilyMemberName = (member) =>
  [member?.firstName, member?.lastName].filter(Boolean).join(" ").trim();

export const getAudienceStudentName = (child) => {
  if (!child || typeof child !== "object") return "";
  const preferred = child?.preferredName?.trim();
  const fullName =
    child?.childName ||
    child?.name ||
    [child?.firstName, child?.lastName].filter(Boolean).join(" ").trim();
  if (!fullName && preferred) return preferred;
  return fullName || "";
};

export const getAudienceStudentNamesFromFamily = (family) =>
  getFamilyChildren(family)
    .map(getAudienceStudentName)
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));

export const getAudienceStudentNamesFromStop = (stop) => {
  const children = stop?.associatedChilds || stop?.children || [];
  return children
    .map(getAudienceStudentName)
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));
};

export const buildFamilyLabelsFromDetails = (familyDetails) => {
  if (!Array.isArray(familyDetails)) return {};

  return familyDetails.reduce((labels, detail) => {
    const id = detail?.familyId || detail?._id;
    if (id) {
      labels[id] = getFamilyShortLabel(detail);
    }
    return labels;
  }, {});
};

export const resolveFamilyLabel = (id, { familyLabels = {}, familyDetails = [] } = {}) => {
  if (familyLabels[id]) return familyLabels[id];

  const detail = familyDetails.find(
    (family) => (family?.familyId || family?._id) === id,
  );
  if (detail) return getFamilyShortLabel(detail);

  return id;
};

export const getFamilyShortLabel = (family) => {
  if (family?.parentName) return family.parentName;

  const parentNamesFromDetails = (family?.parents || [])
    .map((parent) => parent?.parentName)
    .filter(Boolean);
  if (parentNamesFromDetails.length) return parentNamesFromDetails.join(", ");

  const parentNames = (family?.parent || [])
    .map(getFamilyMemberName)
    .filter(Boolean);
  if (parentNames.length) return parentNames.join(", ");

  const childNames = getFamilyChildren(family)
    .map(getFamilyMemberName)
    .filter(Boolean);
  if (childNames.length) return childNames.join(", ");

  const address = family?.address || family?.parent?.[0]?.address;
  return address || "Family";
};

export const getFamilyDisplayLabel = (family) => {
  const parentNames = (family?.parent || [])
    .map(getFamilyMemberName)
    .filter(Boolean);
  if (!parentNames.length && family?.parentName) {
    parentNames.push(family.parentName);
  }
  if (!parentNames.length) {
    const namesFromDetails = (family?.parents || [])
      .map((parent) => parent?.parentName)
      .filter(Boolean);
    parentNames.push(...namesFromDetails);
  }

  const childNames = getFamilyChildren(family)
    .map(getFamilyMemberName)
    .filter(Boolean);
  const parts = [];
  const hasFullFamilyShape =
    Array.isArray(family?.parent) && family.parent.length > 0;

  if (parentNames.length && childNames.length) {
    parts.push(
      childNames.length === 1
        ? `Child: ${childNames[0]}`
        : `Children: ${childNames.join(", ")}`,
    );
  } else if (childNames.length) {
    parts.push(
      childNames.length === 1
        ? `Child: ${childNames[0]}`
        : `Children: ${childNames.join(", ")}`,
    );
  } else if (hasFullFamilyShape) {
    parts.push("No children listed");
  }

  const address = family?.address || family?.parent?.[0]?.address;
  if (address) parts.push(address);

  if (parts.length === 0 && parentNames.length) {
    return parentNames.join(", ");
  }

  return parts.join(" · ");
};

export const getStopShortLabel = (stop) =>
  stop?.stopName?.trim() || stop?.name?.trim() || "Unnamed stop";

export const buildStopLabelsFromDetails = (stopDetails) => {
  if (!Array.isArray(stopDetails)) return {};

  return stopDetails.reduce((labels, detail) => {
    const id = detail?.stopId || detail?._id || detail?.id;
    if (id) {
      labels[id] = getStopShortLabel(detail);
    }
    return labels;
  }, {});
};

export const resolveStopLabel = (
  id,
  { stopLabels = {}, stopDetails = [] } = {},
) => {
  if (stopLabels[id]) return stopLabels[id];

  const detail = stopDetails.find(
    (stop) => (stop?.stopId || stop?._id || stop?.id) === id,
  );
  if (detail) return getStopShortLabel(detail);

  return id;
};

export const getStopAssociatedStudentCount = (stop) => {
  if (typeof stop?.totalChildCount === "number") {
    return stop.totalChildCount;
  }
  if (Array.isArray(stop?.associatedChilds)) {
    return stop.associatedChilds.length;
  }
  return 0;
};

export const getStopDisplayLabel = (stop) => {
  const parts = [];
  const studentCount = getStopAssociatedStudentCount(stop);

  if (studentCount > 0) {
    parts.push(
      `${studentCount} associated student${studentCount === 1 ? "" : "s"}`,
    );
  }

  if (stop?.address?.trim()) {
    parts.push(stop.address.trim());
  }

  return parts.join(" · ");
};

export const CAMPAIGN_FORM_STEPS = [
  {
    key: "details",
    title: "Campaign details",
    description:
      "Internal name and description for your team. Parents only see the message.",
  },
  {
    key: "delivery",
    title: "When to send",
    description: "Choose how this campaign is delivered.",
  },
  {
    key: "audience",
    title: "Who receives this",
    description: "Select the parents who should get this notification.",
  },
  {
    key: "message",
    title: "Message",
    description: "Write the notification parents will receive.",
  },
];

/** Delivery is fixed after create — edit/view flows skip that step. */
export const getCampaignFormSteps = (mode) =>
  mode === "create"
    ? CAMPAIGN_FORM_STEPS
    : CAMPAIGN_FORM_STEPS.filter((step) => step.key !== "delivery");

export const validateCampaignStepByKey = (stepKey, input, institutionTz) => {
  const stepIndex = CAMPAIGN_FORM_STEPS.findIndex(
    (step) => step.key === stepKey,
  );
  if (stepIndex < 0) return { valid: true, message: "" };
  return validateCampaignStep(stepIndex, input, institutionTz);
};

export const validateCampaignStep = (stepIndex, input, institutionTz) => {
  const step = CAMPAIGN_FORM_STEPS[stepIndex];
  if (!step) return { valid: true, message: "" };

  switch (step.key) {
    case "details": {
      if (!input.name?.trim()) {
        return { valid: false, message: "Enter a campaign name." };
      }
      if (input.name.length > 100) {
        return {
          valid: false,
          message: "Campaign name should not exceed 100 characters.",
        };
      }
      if (!input.description?.trim()) {
        return { valid: false, message: "Enter a description." };
      }
      if (input.description.length > 300) {
        return {
          valid: false,
          message: "Description should not exceed 300 characters.",
        };
      }
      return { valid: true, message: "" };
    }
    case "delivery": {
      if (!input.alertType?.trim()) {
        return { valid: false, message: "Choose when to send this campaign." };
      }
      if (input.alertType === "scheduled") {
        if (!input.scheduledDate || !input.scheduledTime) {
          return {
            valid: false,
            message: "Select a scheduled date and time.",
          };
        }
        if (
          !isScheduledDateTimeInFuture(
            input.scheduledDate,
            input.scheduledTime,
            institutionTz,
          )
        ) {
          return {
            valid: false,
            message: "Scheduled time must be in the future.",
          };
        }
      }
      return { valid: true, message: "" };
    }
    case "audience": {
      if (!input.targetAudience?.trim()) {
        return {
          valid: false,
          message: "Select who should receive this campaign.",
        };
      }
      if (!isAudienceSelectionValid(input)) {
        return {
          valid: false,
          message:
            getAudienceSelectionError(input) ||
            "Complete the audience selection.",
        };
      }
      return { valid: true, message: "" };
    }
    case "message": {
      const plainMessage = stripHtmlToPlainText(input.message);
      if (!plainMessage) {
        return { valid: false, message: "Enter a message for parents." };
      }
      if (plainMessage.length > 1200) {
        return {
          valid: false,
          message: "Message should not exceed 1200 characters.",
        };
      }
      return { valid: true, message: "" };
    }
    default:
      return { valid: true, message: "" };
  }
};
